import { computed, inject, Injectable } from '@angular/core';
import { ServerVersionService } from './server-version.service';

/**
 * Returns true if the given server version string meets the minimum requirement
 * for Selector Provider / Reports features (≥ 26.1 equivalent).
 *
 * Supported version formats:
 *   Prod:  X.Y.Z       e.g. 26.1.100, 27.2.600
 *   Stage: 0.Y.B.R     e.g. 0.26.423.1345
 *   Dev:   0.0.0.R     e.g. 0.0.0.900  → always supported
 */
export function isVersionSupported(version: string): boolean {
  if (!version) {
    return false;
  }

  const parts = version.trim().split('.').map(Number);
  if (parts.length < 2 || parts.some((p) => isNaN(p))) {
    return false;
  }

  const [major, minor] = parts;

  // Dev: 0.0.x.x → always supported
  if (major === 0 && minor === 0) {
    return true;
  }

  // Stage: 0.Y.x.x → supported when Y >= 26
  if (major === 0) {
    return minor >= 26;
  }

  // Prod: X.Y.x → supported when X > 26, or X === 26 and Y >= 1
  return major > 26 || (major === 26 && minor >= 1);
}

@Injectable({ providedIn: 'root' })
export class FeatureToggleService {
  private readonly _serverVersionService = inject(ServerVersionService);

  public readonly supportsSelectorProviders = computed(() =>
    isVersionSupported(this._serverVersionService.version())
  );
  public readonly supportsReports = computed(() =>
    isVersionSupported(this._serverVersionService.version())
  );
}
